(function (window, document) {
    'use strict';

    var pointerSession = null;
    var pointerFrame = null;
    var latestPointerEvent = null;
    var renderedTrackId = '';

    function formatTimecode(time) {
        time = Number.isFinite(time) && time >= 0 ? time : 0;

        return Math.floor(time / 60) + ':' + ('00' + Math.floor(time % 60)).slice(-2);
    }

    function publishInteraction(action, row, detail) {
        var eventDetail = detail || {};

        eventDetail.action = action;
        eventDetail.trackId = row ? row.getAttribute('data-trackid') || '' : '';
        eventDetail.title = row ? row.getAttribute('data-title') || '' : '';

        document.dispatchEvent(new CustomEvent('ikson:music-library-interaction', {
            detail: eventDetail
        }));
    }

    var peakCodec = window.iksonWaveformPeaks || {};

    peakCodec.decode = peakCodec.decode || function (encoded) {
        var bytes;
        var peaks = [];
        var index;

        if (!encoded) {
            return peaks;
        }

        try {
            bytes = window.atob(encoded);
        } catch (error) {
            return peaks;
        }

        for (index = 0; index < bytes.length; index += 1) {
            var value = bytes.charCodeAt(index);
            peaks.push((value > 127 ? value - 256 : value) / 127);
        }

        return peaks;
    };

    window.iksonWaveformPeaks = peakCodec;

    function createBars(row) {
        var waveform = row.querySelector('[data-music-library-waveform]');
        var base = row.querySelector('[data-music-library-waveform-bars]');
        var progress = row.querySelector('[data-music-library-waveform-progress-bars]');
        var peaks = peakCodec.decode(row.getAttribute('data-waveform-peaks'));
        var peakCount = Math.floor(peaks.length / 2);
        var availableWidth;
        var barCount;
        var baseFragment;
        var progressFragment;
        var index;

        if (!waveform || !base || !progress || waveform.hasAttribute('data-waveform-ready')) {
            return;
        }

        if (peakCount === 0) {
            waveform.setAttribute('data-waveform-missing', 'true');
            waveform.setAttribute('data-waveform-fallback', 'neutral');
            waveform.setAttribute('data-waveform-ready', 'fallback');

            if (waveform.getAttribute('aria-disabled') !== 'true') {
                waveform.setAttribute(
                    'aria-label',
                    waveform.getAttribute('aria-label') + '. Waveform preview unavailable; playback and seeking remain available.'
                );
            }

            return;
        }

        availableWidth = Math.max(0, waveform.clientWidth - 66);
        barCount = Math.min(peakCount, Math.max(64, Math.floor(availableWidth / 3)));
        baseFragment = document.createDocumentFragment();
        progressFragment = document.createDocumentFragment();

        for (index = 0; index < barCount; index += 1) {
            var peakStart = Math.floor(index * peakCount / barCount);
            var peakEnd = Math.max(peakStart + 1, Math.floor((index + 1) * peakCount / barCount));
            var amplitude = 0;
            var height;
            var baseBar = document.createElement('span');
            var progressBar = document.createElement('span');
            var peakIndex;

            for (peakIndex = peakStart; peakIndex < peakEnd; peakIndex += 1) {
                amplitude = Math.max(amplitude, Math.abs(peaks[peakIndex * 2]), Math.abs(peaks[(peakIndex * 2) + 1]));
            }

            height = Math.round(Math.max(3, Math.min(100, amplitude * 100)));
            baseBar.style.setProperty('--music-library-waveform-bar-height', height + '%');
            progressBar.style.setProperty('--music-library-waveform-bar-height', height + '%');
            baseFragment.appendChild(baseBar);
            progressFragment.appendChild(progressBar);
        }

        base.appendChild(baseFragment);
        progress.appendChild(progressFragment);
        waveform.setAttribute('data-waveform-ready', 'true');
    }

    function initializeWaveforms(scope) {
        var root = scope && scope.querySelectorAll ? scope : document;
        var rows = [];

        if (root.matches && root.matches('[data-music-library-track-row]')) {
            rows.push(root);
        }

        Array.prototype.push.apply(rows, root.querySelectorAll('[data-music-library-track-row]'));
        rows.forEach(createBars);
        renderCurrentState();
    }

    function rowsForTrack(trackId) {
        var selector;

        if (!trackId) {
            return [];
        }

        if (window.CSS && typeof window.CSS.escape === 'function') {
            selector = '[data-music-library-track-row][data-trackid="' + window.CSS.escape(trackId) + '"]';
            return document.querySelectorAll(selector);
        }

        return Array.prototype.filter.call(document.querySelectorAll('[data-music-library-track-row]'), function (row) {
            return row.getAttribute('data-trackid') === trackId;
        });
    }

    function renderTrackRows(trackId, currentTime, mediaDuration) {
        Array.prototype.forEach.call(rowsForTrack(trackId), function (row) {
            var waveform = row.querySelector('[data-music-library-waveform]');
            var rowDuration = Number(row.getAttribute('data-trackseconds')) || mediaDuration || 1;
            var duration = mediaDuration > 0 ? mediaDuration : rowDuration;
            var rowTime = Math.min(duration, currentTime);
            var progress = duration > 0 ? (rowTime / duration) * 100 : 0;

            if (!waveform) {
                return;
            }

            waveform.style.setProperty('--music-library-waveform-progress', progress.toFixed(3) + '%');
            waveform.setAttribute('aria-valuemax', Math.max(1, Math.round(duration)));
            waveform.setAttribute('aria-valuenow', Math.max(0, Math.round(rowTime)));
            waveform.setAttribute('aria-valuetext', formatTimecode(rowTime));
        });
    }

    function renderState(state) {
        var trackId = state && state.trackId ? String(state.trackId) : '';
        var currentTime = state && Number.isFinite(state.currentTime) ? state.currentTime : 0;
        var mediaDuration = state && Number.isFinite(state.duration) ? state.duration : 0;

        if (renderedTrackId && renderedTrackId !== trackId) {
            renderTrackRows(renderedTrackId, 0, 0);
        }

        if (trackId) {
            renderTrackRows(trackId, currentTime, mediaDuration);
        }

        renderedTrackId = trackId;
    }

    function renderCurrentState() {
        if (window.iksonPlayer && typeof window.iksonPlayer.getState === 'function') {
            renderState(window.iksonPlayer.getState());
        } else {
            renderState(null);
        }
    }

    function rowForWaveform(waveform) {
        return waveform && waveform.closest('[data-music-library-track-row]');
    }

    function timeFromClientX(waveform, row, clientX) {
        var rect = waveform.getBoundingClientRect();
        var duration = Number(waveform.getAttribute('aria-valuemax')) || Number(row.getAttribute('data-trackseconds')) || 0;
        var ratio;

        if (!rect.width || duration <= 0) {
            return 0;
        }

        ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
        return ratio * duration;
    }

    function auditionAt(waveform, clientX, input) {
        var row = rowForWaveform(waveform);
        var duration;
        var seconds;

        if (!row
            || waveform.getAttribute('aria-disabled') === 'true'
            || row.getAttribute('data-media-unavailable') === 'true'
            || !window.iksonPlayer
            || typeof window.iksonPlayer.activateTrack !== 'function') {
            return;
        }

        duration = Number(waveform.getAttribute('aria-valuemax')) || Number(row.getAttribute('data-trackseconds')) || 0;
        seconds = timeFromClientX(waveform, row, clientX);
        window.iksonPlayer.activateTrack(row, seconds);

        if (input) {
            publishInteraction('waveform-audition', row, {
                input: input,
                seconds: Math.round(seconds * 100) / 100,
                positionRatio: duration > 0 ? Math.round((seconds / duration) * 1000) / 1000 : 0
            });
        }
    }

    function auditionFromKeyboard(waveform, event) {
        var row = rowForWaveform(waveform);
        var duration;
        var current;
        var next;

        if (!row
            || waveform.getAttribute('aria-disabled') === 'true'
            || row.getAttribute('data-media-unavailable') === 'true'
            || !window.iksonPlayer
            || typeof window.iksonPlayer.activateTrack !== 'function') {
            return;
        }

        duration = Number(waveform.getAttribute('aria-valuemax')) || Number(row.getAttribute('data-trackseconds')) || 0;
        current = Number(waveform.getAttribute('aria-valuenow')) || 0;

        if (event.key === 'Enter') {
            next = current;
        } else if (event.key === 'ArrowLeft') {
            next = Math.max(0, current - 5);
        } else if (event.key === 'ArrowRight') {
            next = Math.min(duration, current + 5);
        } else if (event.key === 'Home') {
            next = 0;
        } else if (event.key === 'End') {
            next = duration;
        } else {
            return;
        }

        event.preventDefault();
        window.iksonPlayer.activateTrack(row, next);
        publishInteraction('waveform-audition', row, {
            input: 'keyboard',
            key: event.key,
            seconds: next,
            positionRatio: duration > 0 ? Math.round((next / duration) * 1000) / 1000 : 0
        });
    }

    function schedulePointerAudition(event) {
        latestPointerEvent = event;

        if (pointerFrame !== null) {
            return;
        }

        pointerFrame = window.requestAnimationFrame(function () {
            pointerFrame = null;

            if (pointerSession && latestPointerEvent) {
                auditionAt(pointerSession.waveform, latestPointerEvent.clientX, null);
            }
        });
    }

    document.addEventListener('pointerdown', function (event) {
        var waveform = event.target.closest('[data-music-library-waveform]');

        if (!waveform
            || waveform.getAttribute('aria-disabled') === 'true'
            || event.button !== 0
            || event.target.closest('[data-music-library-track-toggle]')) {
            return;
        }

        pointerSession = {
            id: event.pointerId,
            waveform: waveform
        };
        waveform.focus({ preventScroll: true });
        waveform.setPointerCapture(event.pointerId);
        event.preventDefault();
        auditionAt(waveform, event.clientX, event.pointerType || 'pointer');
    });

    document.addEventListener('pointermove', function (event) {
        if (!pointerSession || event.pointerId !== pointerSession.id) {
            return;
        }

        event.preventDefault();
        schedulePointerAudition(event);
    });

    function finishPointer(event) {
        if (!pointerSession || event.pointerId !== pointerSession.id) {
            return;
        }

        if (pointerSession.waveform.hasPointerCapture(event.pointerId)) {
            pointerSession.waveform.releasePointerCapture(event.pointerId);
        }

        pointerSession = null;
        latestPointerEvent = null;
    }

    document.addEventListener('pointerup', finishPointer);
    document.addEventListener('pointercancel', finishPointer);

    document.addEventListener('click', function (event) {
        var waveform;
        var row;
        var action;

        if (!window.PointerEvent && event.detail !== 0) {
            waveform = event.target.closest('[data-music-library-waveform]');

            if (waveform) {
                auditionAt(waveform, event.clientX, 'legacy-mouse');
            }
        }

        row = event.target.closest('[data-music-library-track-row]');

        if (!row) {
            return;
        }

        if (event.target.closest('.download-panel-trigger')) {
            action = 'download-intent';
        } else if (event.target.closest('.music-library-audition-row__artwork, .music-library-audition-row__identity a')) {
            action = 'track-details';
        }

        if (action) {
            publishInteraction(action, row, {
                input: event.detail === 0 ? 'keyboard' : 'pointer'
            });
        }
    });

    document.addEventListener('keydown', function (event) {
        var waveform = event.target.closest('[data-music-library-waveform]');

        if (waveform) {
            auditionFromKeyboard(waveform, event);
        }
    });

    document.addEventListener('ikson:player-state', function (event) {
        renderState(event.detail || null);
    });

    document.addEventListener('ikson:music-library-tracks-updated', function () {
        initializeWaveforms(document);
    });

    function init() {
        initializeWaveforms(document);

        if (window.MutationObserver) {
            new MutationObserver(function (mutations) {
                mutations.forEach(function (mutation) {
                    Array.prototype.forEach.call(mutation.addedNodes, function (node) {
                        if (node.nodeType === 1) {
                            initializeWaveforms(node);
                        }
                    });
                });
            }).observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})(window, document);
